import { Formik } from 'formik';
import { useState, useCallback, useContext } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import * as yup from 'yup';
import Common from '../../shared/Common';
import WarningModal from '../../shared/shared-modals/WarningModal';
import type { ILocation } from '../../../interfaces/ILocation';
import type { IIncomeCalendarSynchronization } from '../../../interfaces/IIncomeCalendarSynchronization';
import { DictionaryContext } from '../../../App';
import type { IIncomeCategory } from '../../../interfaces/IIncomeCategory';
import type { IIncomeQuantityType } from '../../../interfaces/IIncomeQuantityType';
import IncomeCalendarsSynchronizationItemsTable from './IncomeCalendarsSynchronizationItemsTable';

import './style.css';

export interface IAddOrUpdateIncomeCalendarsSynchronizationModalProps {
  show: boolean;
  onHide: () => void;
  onSave: () => void;
  selectedIncomeCalendarSynchronization: IIncomeCalendarSynchronization | null;
  incomeQuantityTypes: IIncomeQuantityType[];
  incomeCategories: IIncomeCategory[];
  locationsNames: ILocation[];
}

function AddOrUpdateIncomeCalendarsSynchronization(props: IAddOrUpdateIncomeCalendarsSynchronizationModalProps) {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const { reloadLocationNames } = useContext(DictionaryContext);

  const removeIncomeCalendarsSynchronization = async () => {
    if (!props.selectedIncomeCalendarSynchronization?.id) {
      return;
    }
    setFormLoading(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: props.selectedIncomeCalendarSynchronization?.id,
      }),
    };
    const response = await Common.authorizedFetch(
      'api/calendarSynchronizations/removeIncomeCalendarSynchronization',
      requestOptions
    );
    const data = await response.json();
    if (!data.success) {
      alert(data.errors);
    }
    setFormLoading(false);
    setShowWarningModal(false);
    props.onHide();
    props.onSave();
  };

  const AddOrUpdateIncomeCalendarsSynchronizationForm = useCallback(() => {
    const validationSchema = yup.object().shape({
      nextUpdate: yup.string().required('Pole z następną aktualizacją jest wymagane'),
    });

    let date = new Date();
    if (!!props.selectedIncomeCalendarSynchronization?.nextUpdate) {
      date = new Date(props.selectedIncomeCalendarSynchronization?.nextUpdate ?? '');
    }
    // przez strefę czasową automatycznie ustawia się dzień wstecz przy godzinie 00:00
    date = new Date((date as Date).getTime() - (date as Date).getTimezoneOffset() * 60000);
    const nextUpdate = date.toISOString().split('T')[0];

    return (
      <Formik
        initialValues={{
          name: props.selectedIncomeCalendarSynchronization?.name ?? '',
          url: props.selectedIncomeCalendarSynchronization?.url ?? '',
          nextUpdate: nextUpdate ?? '',
          items: props.selectedIncomeCalendarSynchronization?.items ?? [],
          manualActivation: props.selectedIncomeCalendarSynchronization?.manualActivation ?? false,
          active: props.selectedIncomeCalendarSynchronization?.active ?? true,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          setFormLoading(true);

          let requestBody = '';
          let apiMethod = '';
          if (props.selectedIncomeCalendarSynchronization === null) {
            requestBody = JSON.stringify({
              name: values.name,
              url: values.url,
              nextUpdate: new Date(values.nextUpdate ?? ''),
              items: values.items,
              manualActivation: values.manualActivation,
              active: values.active,
            });
            apiMethod = 'api/calendarSynchronizations/addIncomeCalendarSynchronization';
          } else {
            requestBody = JSON.stringify({
              id: props.selectedIncomeCalendarSynchronization?.id ?? 0,
              name: values.name,
              url: values.url,
              nextUpdate: new Date(values.nextUpdate ?? ''),
              items: values.items,
              manualActivation: values.manualActivation,
              active: values.active,
            });
            apiMethod = 'api/calendarSynchronizations/saveIncomeCalendarSynchronization';
          }

          const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: requestBody,
          };
          try {
            const response = await Common.authorizedFetch(apiMethod, requestOptions);
            const data = await response.json();
            if (!data.success) {
              alert(data.errors);
            } else {
              if (data.result.newLocationAdded && reloadLocationNames) {
                reloadLocationNames();
              }
              props.onSave();
            }
          } catch (e) {
            alert(e);
          } finally {
            props.onHide();
            setFormLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <>
            <Form id='calendar-synchronization-form' onSubmit={handleSubmit}>
              <Form.Group className='mt-1'>
                <Form.Label>Nazwa synchronizacji kalendarza</Form.Label>
                <Form.Control
                  autoComplete='on'
                  type='text'
                  name='name'
                  placeholder='Wpisz nazwę operacji'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  className={touched.name && errors.name ? 'error' : undefined}
                />
                {touched.name && errors.name ? <div className='error-message'>{errors.name}</div> : null}
                <Form.Text className='text-muted'>
                  Nazwa synchronizacji kalendarza, która będzie wyświetlana (pole opcjonalne).
                </Form.Text>
              </Form.Group>
              <Form.Group className='mt-1'>
                <Form.Label>Adres url</Form.Label>
                <Form.Control
                  autoComplete='on'
                  type='text'
                  name='url'
                  placeholder='Wprowadź url'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.url}
                  className={touched.url && errors.url ? 'error' : undefined}
                />
                {touched.url && errors.url ? <div className='error-message'>{errors.url}</div> : null}
                <Form.Text className='text-muted'>Adres url do pliku .ics z kalendarzem.</Form.Text>
              </Form.Group>
              <Form.Group>
                <IncomeCalendarsSynchronizationItemsTable
                  items={values.items}
                  setItems={(items) => {
                    setFieldValue('items', items);
                  }}
                  quantityTypes={props.incomeQuantityTypes}
                  categories={props.incomeCategories}
                  locations={props.locationsNames}
                  updateIncomeCalendarSynchronization={(rowIndex: number, columnId: string, value: any) => {
                    const updatedItems = [...values.items];
                    updatedItems[rowIndex] = {
                      ...updatedItems[rowIndex],
                      [columnId]: value,
                    };
                    setFieldValue('items', updatedItems);
                  }}
                />
              </Form.Group>
              <Form.Group className='mt-1'>
                <Form.Check
                  type='switch'
                  id='manual-activation-switch'
                  name='manualActivation'
                  label='Ręczne uruchamianie'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  checked={values.manualActivation}
                  className={touched.manualActivation && errors.active ? 'error' : undefined}
                />
                {touched.manualActivation && errors.manualActivation ? (
                  <div className='error-message'>{errors.manualActivation}</div>
                ) : null}
                <Form.Text className='text-muted'>
                  Czy włączyć ręczne generowanie przychodów (zamiast automatycznego).
                </Form.Text>
              </Form.Group>
              <Form.Group className='mt-1'>
                <Form.Label>Następna aktualizacja</Form.Label>
                <Form.Control
                  type='date'
                  name='nextUpdate'
                  placeholder='Wpisz interwał'
                  disabled={!values.active || values.manualActivation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.nextUpdate}
                  className={touched.nextUpdate && errors.nextUpdate ? 'error' : undefined}
                />
                {touched.nextUpdate && errors.nextUpdate ? (
                  <div className='error-message'>{errors.nextUpdate}</div>
                ) : null}
                <Form.Text className='text-muted'>
                  Kiedy ma zostać przeprowadzona następna synchronizacja kalendarza.
                </Form.Text>
              </Form.Group>
              <Form.Group className='mt-1'>
                <Form.Check
                  type='switch'
                  id='active-switch'
                  name='active'
                  label='Aktywna'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  checked={values.active}
                  className={touched.active && errors.active ? 'error' : undefined}
                />
                {touched.active && errors.active ? <div className='error-message'>{errors.active}</div> : null}
              </Form.Group>
            </Form>
          </>
        )}
      </Formik>
    );
  }, [props, reloadLocationNames]);

  return (
    <>
      <Modal
        show={props.show}
        onHide={props.onHide}
        aria-labelledby='contained-modal-title-vcenter'
        size='lg'
        style={{ maxWidth: '100%', overflowX: 'auto', ...(showWarningModal ? { zIndex: 1049 } : {}) }}
      >
        <Modal.Header closeButton>
          <Modal.Title id='contained-modal-title-vcenter'>
            {props.selectedIncomeCalendarSynchronization === null
              ? 'Dodaj nową synchronizacje kalendarza'
              : 'Aktualizacja synchronizacji kalendarza'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Common.Ui.showLoadingSpinnerAbsolute(formLoading)}
          <AddOrUpdateIncomeCalendarsSynchronizationForm />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='dark' onClick={props.onHide}>
            Zamknij
          </Button>
          {props.selectedIncomeCalendarSynchronization && (
            <Button
              variant='danger'
              onClick={() => {
                setShowWarningModal(true);
              }}
            >
              Usuń
            </Button>
          )}
          <Button variant='success' type='submit' form='calendar-synchronization-form'>
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
      <WarningModal
        show={showWarningModal}
        modalBodyText={'Czy na pewno chcesz usunąć synchronizację kalendarza?'}
        onHide={() => {
          setShowWarningModal(false);
        }}
        onConfirmation={() => removeIncomeCalendarsSynchronization()}
      />
    </>
  );
}

export default AddOrUpdateIncomeCalendarsSynchronization;
